/**
 * Pure CTA intelligence: intent scoring, label enrichment, adaptive defaults by surface.
 * Used by resolveSectionButtonsFromContent — single source of truth for conversion-oriented behavior.
 * (No import from sectionButtonConfig — avoids circular deps.)
 */

export type SectionCtaSurface = "hero" | "product" | "footer" | "banner" | "default";

/** Options for `resolveSectionButtonsFromContent` / `resolveButtons`. */
export interface ResolveSectionButtonsOptions {
    /** When set, adaptive variant/size may apply (unless masked or disabled). */
    sectionKind?: SectionCtaSurface;
    /** Default true when used with `sectionKind`. Set false to skip adaptive styling. */
    adaptiveDefaults?: boolean;
    /** Default true — sort by conversion intent (highest first). */
    prioritizeByIntent?: boolean;
    /** Default true — replace empty / weak labels. */
    enrichLabels?: boolean;
    fallbackLabel?: string;
}

type CtaVariant = "primary" | "secondary" | "outline" | "ghost";
type CtaSize = "sm" | "md" | "lg";

/** FR-first storefront; override via resolve options. */
export const DEFAULT_CTA_FALLBACK_LABEL = "En savoir plus";

const WEAK_LABELS = new Set(
    [
        "click here",
        "cliquez",
        "cliquez ici",
        "ici",
        "more",
        "suite",
        "...",
        "…",
        "link",
        "lien",
        "go",
        "ok",
        "yes",
        "oui",
        "no",
        "non",
    ].map((s) => s.toLowerCase())
);

const HIGH_INTENT = [
    /\bachetez\b/i,
    /\bacheter\b/i,
    /\bbuy\b/i,
    /\bcheckout\b/i,
    /\bcommander\b/i,
    /\bcommandez\b/i,
    /\border now\b/i,
    /\bpanier\b/i,
    /\bcart\b/i,
    /\badd to cart\b/i,
    /\bajouter\b/i,
    /\bpayer\b/i,
    /\bpay now\b/i,
    /\bshop now\b/i,
    /\bsubscribe\b/i,
    /\bm'abonner\b/i,
    /\bs'inscrire\b/i,
];

const MID_INTENT = [
    /\bshop\b/i,
    /\bboutique\b/i,
    /\bmagasin\b/i,
    /\bdécouvrir\b/i,
    /\bdiscover\b/i,
    /\bvoir\b/i,
    /\bview\b/i,
    /\bexplorer\b/i,
    /\bget started\b/i,
    /\bcommencer\b/i,
];

export interface AdaptivePair {
    primaryVariant: CtaVariant;
    primarySize: CtaSize;
    secondaryVariant: CtaVariant;
    secondarySize: CtaSize;
}

const ADAPTIVE: Record<SectionCtaSurface, AdaptivePair> = {
    hero: {
        primaryVariant: "primary",
        primarySize: "lg",
        secondaryVariant: "outline",
        secondarySize: "lg",
    },
    product: {
        primaryVariant: "secondary",
        primarySize: "md",
        secondaryVariant: "outline",
        secondarySize: "md",
    },
    footer: {
        primaryVariant: "ghost",
        primarySize: "sm",
        secondaryVariant: "ghost",
        secondarySize: "sm",
    },
    banner: {
        primaryVariant: "primary",
        primarySize: "md",
        secondaryVariant: "outline",
        secondarySize: "md",
    },
    default: {
        primaryVariant: "primary",
        primarySize: "md",
        secondaryVariant: "secondary",
        secondarySize: "md",
    },
};

export function getAdaptiveCtaDefaults(surface: SectionCtaSurface): AdaptivePair {
    return ADAPTIVE[surface] ?? ADAPTIVE.default;
}

/** 0–100; higher = stronger conversion intent (buy / checkout / cart). */
export function ctaIntentScore(text: string): number {
    const t = typeof text === "string" ? text : "";
    if (!t.trim()) return 0;
    let s = 0;
    for (const re of HIGH_INTENT) {
        if (re.test(t)) s += 42;
    }
    for (const re of MID_INTENT) {
        if (re.test(t)) s += 18;
    }
    return Math.min(100, s);
}

/**
 * Highest-intent CTAs first (stable for equal scores).
 */
export function prioritizeCtasByIntent<T extends { text: string }>(buttons: T[]): T[] {
    if (buttons.length <= 1) return buttons;
    return [...buttons]
        .map((b, idx) => ({ b, idx, score: ctaIntentScore(b.text) }))
        .sort((a, c) => {
            if (c.score !== a.score) return c.score - a.score;
            return a.idx - c.idx;
        })
        .map((x) => x.b);
}

export function isWeakCtaLabel(text: string): boolean {
    const t = typeof text === "string" ? text.trim() : "";
    if (!t) return true;
    if (t.length <= 1) return true;
    const lower = t.toLowerCase();
    if (WEAK_LABELS.has(lower)) return true;
    if (/^\.{3}$|^…$/.test(t)) return true;
    return false;
}

export function ensureMeaningfulCtaLabel(raw: unknown, fallback: string = DEFAULT_CTA_FALLBACK_LABEL): string {
    try {
        const t = typeof raw === "string" ? raw.trim() : "";
        if (!t || isWeakCtaLabel(t)) return fallback;
        return t;
    } catch {
        return fallback;
    }
}

export function enrichSectionButtonLabels<T extends { text: string }>(
    buttons: T[],
    fallback: string
): T[] {
    try {
        return buttons.map((b) => ({
            ...b,
            text: ensureMeaningfulCtaLabel(b.text, fallback),
        }));
    } catch {
        return buttons;
    }
}

/**
 * Apply surface defaults only where the editor did not set variant/style explicitly.
 */
export function applyAdaptiveCtaStyling<T extends { variant: CtaVariant; size: CtaSize }>(
    buttons: T[],
    surface: SectionCtaSurface | undefined,
    explicitMask: boolean[]
): T[] {
    if (!surface || buttons.length === 0) return buttons;
    try {
        const d = getAdaptiveCtaDefaults(surface);
        return buttons.map((b, i) => {
            if (explicitMask[i]) return b;
            if (i === 0) {
                return {
                    ...b,
                    variant: d.primaryVariant,
                    size: d.primarySize,
                };
            }
            return {
                ...b,
                variant: d.secondaryVariant,
                size: d.secondarySize,
            };
        });
    } catch {
        return buttons;
    }
}
